import { Controller } from "stimulus";
import { followCategory } from "../following_categories/shared_functions";
import { debounce, getCSRFToken } from "../../util";
import {
  SELECT_BULK_FOLLOW_CATEGORY_CTA,
  SELECT_FOLLOW_CATEGORY_CTA_EVENT,
  VIEW_MODAL_EVENT
} from "../../constants/mixpanel_config";
import { trackEvent } from "../../util/analytics";

export default class extends Controller {
  static values = {
    noResultsImage: String,
  }

  static targets = [
    // Search bar
    "searchInput",

    // Categories
    "searchResultsContainer", "loading",
    "followCategoryRow", "followButton", "categoriesCounter"
  ];

  _ajaxCount = 0;
  _selectedCategoriesFromModal = [];

  connect() {
    this.handleSearchInput = debounce(this._handleSearchInput.bind(this), 300);
  }

  closeModal() {
    if (this._selectedCategoriesFromModal.length > 0) {
      Modal.confirm(
        I18n.t("following_categories.exit_modal_title"),
        I18n.t("following_categories.exit_modal_subtitle"),
        I18n.t("following_categories.continue_selecting"),
        I18n.t("following_categories.close_anyway"),
      ).then((result) => {
        if (result.confirm || result?.source === "click_away") return;

        // Clear selections when switching
        this._selectedCategoriesFromModal = [];

        // User confirmed to close modal
        Modal.hide("following_categories_add_category");
        this._updateSelectionIcon();
      });
    } else {
      Modal.hide("following_categories_add_category");
    }
  }

  openModal() {
    Modal.show('following_categories_add_category')
    this._searchQuery(null, this._ajaxCount)
    trackEvent(VIEW_MODAL_EVENT, { modal_name: "category_follow_search" })
  }

  handleCategorySelect(e) {
    const categorySlug = e.currentTarget.dataset.categorySlug;

    if (this._selectedCategoriesFromModal.includes(categorySlug)) {
      this._removeSlugFromArray(categorySlug, this._selectedCategoriesFromModal);
    } else {
      this._selectedCategoriesFromModal.push(categorySlug);
      trackEvent(SELECT_FOLLOW_CATEGORY_CTA_EVENT, { origin: "category_follow_search", category_name: categorySlug })
    }

    this._updateSelectionIcon();
    this._updateCoinsCounter();
    this._updateButtonsAvailability();
  }

  followCategories() {
    trackEvent(SELECT_BULK_FOLLOW_CATEGORY_CTA, {})
    followCategory(() => {
      Modal.hide("following_categories_add_category");
      window.location.reload();
    }, this._selectedCategoriesFromModal, false);
  }

  _handleSearchInput() {
    const query = this.searchInputTarget.value;

    this._ajaxCount += 1;
    const ajaxCount = this._ajaxCount;

    this._searchQuery(query, ajaxCount)
  }

  _updateSelectionIcon() {
    this.element.querySelectorAll(`i.selection-icon`).forEach(circle => {
      const categorySlug = circle.getAttribute("data-category-slug");
      if (!categorySlug) return;

      if (this._selectedCategoriesFromModal.includes(categorySlug)) {
        circle.classList.remove("far", "fa-circle", "tw-text-gray-400");
        circle.classList.add("fas", "fa-circle-check", "tw-text-primary-500", "dark:tw-text-primary-400");
      } else {
        circle.classList.remove("fas", "fa-circle-check", "tw-text-primary-500", "dark:tw-text-primary-400");
        circle.classList.add("far", "fa-circle", "tw-text-gray-400");
      }
    });
  }

  _updateCoinsCounter() {
    if (!this.categoriesCounterTarget) return;

    this.categoriesCounterTarget.innerHTML = I18n.t("following_categories.categories_selected", {
      count: this._selectedCategoriesFromModal.length
    });
  }

  _updateButtonsAvailability() {
    this.followButtonTarget.disabled = this._selectedCategoriesFromModal.length <= 0;
  }

  _removeSlugFromArray(categorySlug, array) {
    let categorySlugIndex = array.indexOf(categorySlug);

    if (categorySlugIndex !== -1) {
      array.splice(categorySlugIndex, 1);
    }
  }

  _noResultFoundHTML() {
    return `
  <div class="tw-flex tw-flex-col tw-gap-6 tw-pt-6 tw-pb-8 tw-items-center">
    <img src="${this.noResultsImageValue}" height="200" width="200" loading="lazy" alt="No coins found">
    <div class="tw-text-center tw-subtitle2">
      ${I18n.t("following_categories.no_results")}
    </div>
  </div>
`;
  }

  _searchQuery(query, ajaxCount) {
    this.loadingTarget.classList.remove("!tw-hidden");

    const params = {
      locale: I18n.locale
    };

    if (query) {
      params.query = query;
    }

    const url = `/${I18n.locale}/following_categories/search_categories`;
    const queryString = new URLSearchParams(params).toString();
    const fullUrl = `${url}?${queryString}`;

    fetch(fullUrl, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken()
      }
    })
      .then(response => response.text())
      .then(result => {
        this.loadingTarget.classList.add("!tw-hidden");
        this.searchResultsContainerTarget.classList.remove("tw-hidden");

        if (!result || this._ajaxCount !== ajaxCount) {
          this.searchResultsContainerTarget.innerHTML = this._noResultFoundHTML();
          return;
        }

        this.searchResultsContainerTarget.innerHTML = result;

        this._updateSelectionIcon();
      });
  }
}
